import { Arrow, Group, Rect, Text } from 'react-konva';
import type { ChartDimensions } from './types';

/** Tick increment for the X axis (deviation, 0–100). */
export const X_TICK_INCREMENT = 5;
/** Tick increment for the Y axis (DI difference, dynamic range). */
export const Y_TICK_INCREMENT = 2;

export type AxesProps = {
    chartDimensions: ChartDimensions;
    diDiffMax: number;
    diDiffRange: number;
    deviationMax: number;
    deviationRange: number;
};

export function Axes({ chartDimensions, diDiffMax, diDiffRange, deviationMax, deviationRange }: AxesProps) {
    const { xOrigin, yOrigin, left, right, top, bottom, width, height } = chartDimensions;

    // X-axis ticks — deviation range is dynamic, centered on 50
    // This offset by 50 is required to cancel out the offset used in `useRange`.
    const deviationMin = 50 - deviationMax;
    const xTickCount = Math.round(deviationRange / X_TICK_INCREMENT);
    const xTicks = Array.from({ length: xTickCount + 1 }, (_, i) => deviationMin + i * X_TICK_INCREMENT);

    // Y-axis ticks — diDiff range is dynamic
    const yTickCount = Math.round(diDiffRange / Y_TICK_INCREMENT);
    const yTicks = Array.from({ length: yTickCount + 1 }, (_, i) => diDiffMax - i * Y_TICK_INCREMENT);

    /**
     * Extend the arrows by 12 in both directions for aesthetic purposes,
     * making them slightly grow out of the chart area.
     */
    const arrowExtend = 12;

    const tickColor = '#999';
    const tickWidth = 6;
    const tickHeight = 1;

    const tickLabelColor = '#666';
    const tickLabelFontSize = 10;
    const tickLabelWidth = 30;

    const axisNameFontSize = 16;
    const axisNameFontColor = '#444';
    const axisNameLineHeight = 1.2;

    return (
        <>
            <Arrow points={[left - arrowExtend, yOrigin, right + arrowExtend, yOrigin]} stroke="#777" strokeWidth={2} />
            <Arrow points={[xOrigin, bottom + arrowExtend, xOrigin, top - arrowExtend]} stroke="#777" strokeWidth={2} />

            {/* Y-axis ticks — diDiff range is dynamic */}
            {yTicks.map((val) => {
                const y = top + ((diDiffMax - val) / diDiffRange) * height;
                return (
                    <Group key={val}>
                        {/* Draw the tick mark, centering it horizontally over the axis, and down half its height */}
                        <Rect
                            x={xOrigin - 3}
                            y={y - tickHeight / 2}
                            width={tickWidth}
                            height={tickHeight}
                            fill={tickColor}
                        />
                        {/* Move the label to the left of the axis, and slightly down to center the tick */}
                        <Text
                            x={xOrigin - 36}
                            y={y - 6}
                            width={tickLabelWidth}
                            text={val > 0 ? `+${val}` : String(val)}
                            fontSize={tickLabelFontSize}
                            fill={tickLabelColor}
                            align="right"
                        />
                    </Group>
                );
            })}

            {/* X-axis ticks — deviation range is dynamic */}
            {xTicks.map((val) => {
                const x = left + ((val - 50 + deviationMax) / deviationRange) * width;
                return (
                    <Group key={val}>
                        {/* Draw the tick mark, centering it vertically over the axis, and left half its width */}
                        <Rect
                            x={x - tickWidth / 2}
                            y={yOrigin - 3}
                            width={tickHeight}
                            height={tickWidth}
                            fill={tickColor}
                        />
                        {/* Move the label below the axis, and center it under the tick */}
                        {/* Also display a label for every other tick to reduce clutter */}
                        {val % (X_TICK_INCREMENT * 2) === 0 && (
                            <Text
                                x={x - 15}
                                y={yOrigin + 6}
                                width={tickLabelWidth}
                                text={String(val)}
                                fontSize={tickLabelFontSize}
                                fill={tickLabelColor}
                                align="center"
                            />
                        )}
                    </Group>
                );
            })}

            <Text
                x={left}
                width={width}
                text={'GAP\n今回DI −前回DI'}
                align="center"
                fontSize={axisNameFontSize}
                fontStyle="bold"
                fill={axisNameFontColor}
                lineHeight={axisNameLineHeight}
            />
            <Text
                // Slightly move the label to the right for aesthetic reasons
                x={right + 20}
                y={top}
                height={height}
                verticalAlign="middle"
                text={'偏差値\n（今回）'}
                align="center"
                fontSize={axisNameFontSize}
                fontStyle="bold"
                fill={axisNameFontColor}
                lineHeight={axisNameLineHeight}
            />
        </>
    );
}
